import { useEffect, useState } from "react";
import {
  ApiError,
  addEnsembleComponent,
  createEnsemble,
  fetchComponentEligibility,
  fetchEnsembleDetail,
  fetchEnsembles,
  removeEnsembleComponent,
  type EnsembleDetailDto,
  type EnsembleSummaryDto,
  type ModelSummaryDto,
} from "../api";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";
import { exportRowsAsCsv } from "../exportCsv";
import { sortGlyph, useSort } from "../hooks/useSort";

type View = { kind: "registry" } | { kind: "create" } | { kind: "edit"; ensembleId: string };

const OUTPUT_TYPES = ["return", "volatility"] as const;
const LEVELS = ["stock", "index", "macro"] as const;

function slotKeyFor(level: string, target: string, horizon: string, outputType: string): string {
  if (!target || !horizon) return "—";
  return `${level}.${target}.${horizon}.${outputType}`;
}

export function EnsembleBuilderPage({ token, onUnauthorized }: { token: string; onUnauthorized: () => void }) {
  const [view, setView] = useState<View>({ kind: "registry" });
  const [roster, setRoster] = useState<EnsembleSummaryDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  function handleAuthAwareError(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      onUnauthorized();
      return true;
    }
    return false;
  }

  function loadRoster() {
    fetchEnsembles(token)
      .then(setRoster)
      .catch((err) => {
        if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "request failed");
      });
  }

  useEffect(() => {
    if (view.kind === "registry") loadRoster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, view.kind]);

  const { sorted: sortedRoster, sortKey, sortDir, toggleSort } = useSort<EnsembleSummaryDto>(
    roster ?? [],
    (row, key) => (row as unknown as Record<string, unknown>)[key],
  );

  function exportRosterCsv() {
    exportRowsAsCsv(
      (roster ?? []) as unknown as Record<string, unknown>[],
      [
        { key: "name", label: "Ensemble" },
        { key: "level", label: "Level" },
        { key: "slot_key", label: "Slot" },
        { key: "n_components", label: "Components" },
        { key: "is_champion", label: "Champion" },
      ],
      "ensembles.csv",
    );
  }

  if (error) return <div className="error" style={{ margin: 14 }}>{error}</div>;

  if (view.kind === "registry") {
    return (
      <>
        <div className="page-head">
          <div>
            <h1>Ensembles</h1>
            <div className="page-sub">Every ensemble across stock segments, indices, and macro. Compose-only — components come from already-built models.</div>
          </div>
          <button className="btn primary" onClick={() => setView({ kind: "create" })}>+ New ensemble</button>
        </div>

        <PageHelper>
          Compose ensembles from already-built models sharing the exact same <FieldHelp term="slot" />slot. Blending
          uses <FieldHelp term="within_slot_blend" />agreement/dispersion, never a vote count — see a component's own
          page for details.
        </PageHelper>

        <div className="card" style={{ padding: 0 }}>
          <div className="h3" style={{ padding: "16px 16px 0" }}>
            <span>Ensemble registry</span>
            <button className="btn ghost sm" onClick={exportRosterCsv}>↓ CSV</button>
          </div>
          <table className="stocks-table">
            <thead>
              <tr>
                <th className="l clickable" onClick={() => toggleSort("name")}>Ensemble {sortGlyph("name", sortKey, sortDir)}</th>
                <th className="clickable" onClick={() => toggleSort("level")}>Level {sortGlyph("level", sortKey, sortDir)}</th>
                <th className="l clickable" onClick={() => toggleSort("slot_key")}>Slot <FieldHelp term="slot" /> {sortGlyph("slot_key", sortKey, sortDir)}</th>
                <th className="r clickable" onClick={() => toggleSort("n_components")}>Components {sortGlyph("n_components", sortKey, sortDir)}</th>
                <th>Status <FieldHelp term="champion_challenger" /></th>
              </tr>
            </thead>
            <tbody>
              {sortedRoster.map((e) => (
                <tr key={`${e.model_id}:${e.version}`} className="clickable" style={{ cursor: "pointer" }} onClick={() => setView({ kind: "edit", ensembleId: e.model_id })}>
                  <td className="l" style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>{e.name ?? e.model_id}</td>
                  <td><span className="chip">{e.level}</span></td>
                  <td className="l mono" style={{ fontSize: "0.8rem", color: "var(--ink-soft)" }}>{e.slot_key}</td>
                  <td className="r mono">{e.n_components}</td>
                  <td><span className={`chip ${e.is_champion ? "st-champion" : "st-challenger"}`}>{e.is_champion ? "champion" : "draft"}</span></td>
                </tr>
              ))}
              {roster && roster.length === 0 && (
                <tr><td colSpan={5} className="mut" style={{ textAlign: "center", padding: 24 }}>No ensembles yet — click "+ New ensemble" to compose one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  if (view.kind === "create") {
    return <CreateSlot token={token} onUnauthorized={onUnauthorized} onCreated={(id) => setView({ kind: "edit", ensembleId: id })} onBack={() => setView({ kind: "registry" })} />;
  }

  return <ComposeEnsemble token={token} ensembleId={view.ensembleId} onUnauthorized={onUnauthorized} onBack={() => setView({ kind: "registry" })} />;
}

function CreateSlot({
  token, onUnauthorized, onCreated, onBack,
}: {
  token: string; onUnauthorized: () => void; onCreated: (id: string) => void; onBack: () => void;
}) {
  const [level, setLevel] = useState<string>("stock");
  const [target, setTarget] = useState("");
  const [horizon, setHorizon] = useState("5");
  const [outputType, setOutputType] = useState<string>("return");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ensembleId = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
  const slotKey = slotKeyFor(level, target, horizon, outputType);

  async function submit() {
    if (!name.trim() || !target.trim() || !horizon) {
      setError("Name, target, and horizon are required.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const ens = await createEnsemble(token, {
        ensemble_id: ensembleId, name: name.trim(), level, target_ref: target.trim(),
        horizon: Number(horizon), output_type: outputType,
      });
      onCreated(ens.model_id);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onUnauthorized();
        return;
      }
      setError(err instanceof ApiError ? err.message : "create failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button className="btn ghost sm" onClick={onBack} style={{ marginBottom: 10 }}>← Ensembles</button>
      <div className="page-head">
        <div>
          <h1>New ensemble</h1>
          <div className="page-sub">Declare the slot first — this fixes which models are eligible as components (ui_spec F6.3).</div>
        </div>
      </div>

      <div className="card">
        <div className="h3"><span>1 · Declare the slot <FieldHelp term="slot" /> <span className="note">level · target · horizon · output_type</span></span></div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <label className="note" style={{ display: "block", marginBottom: 4 }}>Level</label>
            <select value={level} onChange={(e) => setLevel(e.target.value)}>
              {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="note" style={{ display: "block", marginBottom: 4 }}>
              Target ref <span style={{ fontSize: "0.7rem" }}>({level === "stock" ? "segment key, e.g. sector:industrials" : level === "index" ? "e.g. IDX:NIFTY" : "macro series key"})</span>
            </label>
            <input className="txt" value={target} onChange={(e) => setTarget(e.target.value)} placeholder={level === "stock" ? "sector:industrials" : level === "index" ? "IDX:NIFTY" : "india_gdp"} style={{ minWidth: 220 }} />
          </div>
          <div>
            <label className="note" style={{ display: "block", marginBottom: 4 }}>Horizon (days)</label>
            <input className="txt" type="number" min={1} value={horizon} onChange={(e) => setHorizon(e.target.value)} style={{ width: 90 }} />
          </div>
          <div>
            <label className="note" style={{ display: "block", marginBottom: 4 }}>Output type</label>
            <select value={outputType} onChange={(e) => setOutputType(e.target.value)}>
              {OUTPUT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="note" style={{ display: "block", marginBottom: 4 }}>Name</label>
            <input className="txt" value={name} onChange={(e) => setName(e.target.value)} placeholder="ensemble name" style={{ minWidth: 220 }} />
          </div>
          <div className="mono" style={{ fontSize: "0.78rem", color: "var(--accent)", background: "var(--surface-2)", border: "1px solid var(--rule)", borderRadius: 7, padding: "8px 12px" }}>
            slot: {slotKey}
          </div>
        </div>
        {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}
        <div style={{ marginTop: 14 }}>
          <button className="btn primary sm" disabled={busy} onClick={submit}>
            {busy ? "Creating…" : "Confirm slot →"}
          </button>
        </div>
      </div>
    </>
  );
}

function fmtSignal(v: number | null): string {
  return v === null ? "—" : `${v >= 0 ? "+" : ""}${(v * 100).toFixed(2)}%`;
}

function ComposeEnsemble({
  token, ensembleId, onUnauthorized, onBack,
}: {
  token: string; ensembleId: string; onUnauthorized: () => void; onBack: () => void;
}) {
  const [detail, setDetail] = useState<EnsembleDetailDto | null>(null);
  const [eligible, setEligible] = useState<ModelSummaryDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [tryModelId, setTryModelId] = useState("");
  const [tryError, setTryError] = useState<string | null>(null);
  const [tryOk, setTryOk] = useState<string | null>(null);

  function handleAuthAwareError(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      onUnauthorized();
      return true;
    }
    return false;
  }

  function reload() {
    fetchEnsembleDetail(token, ensembleId)
      .then((d) => {
        setDetail(d);
        return fetchComponentEligibility(token, d.slot_key ?? "");
      })
      .then(setEligible)
      .catch((err) => {
        if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "request failed");
      });
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, ensembleId]);

  async function addEligible(m: ModelSummaryDto) {
    setAddError(null);
    try {
      await addEnsembleComponent(token, ensembleId, {
        child_kind: "model", child_id: m.model_id, child_version: m.version, column_type: "regressor",
      });
      reload();
    } catch (err) {
      if (!handleAuthAwareError(err)) setAddError(err instanceof ApiError ? err.message : "add failed");
    }
  }

  async function remove(childKind: string, childId: string, childVersion: string) {
    try {
      await removeEnsembleComponent(token, ensembleId, childKind, childId, "1", childVersion);
      reload();
    } catch (err) {
      if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "remove failed");
    }
  }

  async function tryIneligible() {
    setTryError(null);
    setTryOk(null);
    if (!tryModelId.trim()) return;
    try {
      await addEnsembleComponent(token, ensembleId, {
        child_kind: "model", child_id: tryModelId.trim(), child_version: "1", column_type: "regressor",
      });
      setTryOk(`${tryModelId.trim()} was added -- it turned out to satisfy this slot after all.`);
      reload();
    } catch (err) {
      if (handleAuthAwareError(err)) return;
      setTryError(err instanceof ApiError ? err.message : "request failed");
    }
  }

  if (error) return <div className="error" style={{ margin: 14 }}>{error}</div>;
  if (!detail) return <div className="page-sub">Loading…</div>;

  const addedIds = new Set(detail.components.map((c) => c.child_id));
  const stillEligible = (eligible ?? []).filter((m) => !addedIds.has(m.model_id));

  return (
    <>
      <button className="btn ghost sm" onClick={onBack} style={{ marginBottom: 10 }}>← Ensembles</button>
      <div className="page-head">
        <div>
          <h1>{detail.name ?? detail.model_id}</h1>
          <div className="page-sub">
            <span className="mono">{detail.slot_key}</span> — components must share this exact slot (level · target · horizon · output_type).
          </div>
        </div>
        <span className={`chip ${detail.is_champion ? "st-champion" : "st-challenger"}`}>{detail.is_champion ? "champion" : "draft"}</span>
      </div>

      <div className="card">
        <div className="h3"><span>Components <span className="note">same-slot, same-type only — enforced server-side, not just filtered here</span></span></div>
        {detail.components.length === 0 ? (
          <div className="empty-state">
            <div className="headline">No components yet</div>
            <div>Add a model built for this exact slot below.</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="stocks-table">
              <thead><tr><th className="l">Model</th><th>Type</th><th className="r">Latest mean prediction</th><th>As of</th><th></th></tr></thead>
              <tbody>
                {detail.components.map((c) => (
                  <tr key={`${c.child_kind}:${c.child_id}:${c.child_version}`}>
                    <td className="l mono" style={{ fontWeight: 600 }}>{c.child_id}</td>
                    <td><span className="typ">{c.column_type}</span></td>
                    <td className="r mono">
                      {c.latest_mean_prediction !== null ? `${fmtSignal(c.latest_mean_prediction)} (n=${c.latest_n})` : <span className="mut">no scored prediction yet</span>}
                    </td>
                    <td className="mono" style={{ fontSize: "0.76rem", color: "var(--ink-faint)" }}>{c.latest_run_date ?? "—"}</td>
                    <td><button className="btn ghost sm" onClick={() => remove(c.child_kind, c.child_id, c.child_version)}>Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <div className="note" style={{ marginBottom: 6 }}>Add a component — eligible models for this slot only:</div>
          {addError && <div className="error" style={{ marginBottom: 8 }}>{addError}</div>}
          {stillEligible.length === 0 ? (
            <div className="empty-state">
              <div className="headline">No eligible models left</div>
              <div>
                {eligible && eligible.length === 0
                  ? "No model has been built for this exact slot yet — build one in the Model Builder and it will appear here automatically."
                  : "Every eligible model for this slot has already been added."}
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {stillEligible.map((m) => (
                <div key={m.model_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", border: "1px solid var(--rule)", borderRadius: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div className="mono" style={{ fontWeight: 600, fontSize: "0.86rem" }}>{m.model_id}</div>
                    <div className="note" style={{ fontSize: "0.72rem" }}>{m.slot_key} · {m.is_champion ? "champion" : "not champion"}</div>
                  </div>
                  <button className="btn ghost sm" onClick={() => addEligible(m)}>+ Add</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="h3"><span>Try an ineligible model <span className="note">demonstrates the enforcement — a rejected add shows the reason, never a silent accept</span></span></div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input className="txt" placeholder="e.g. a σ model or a different segment's model_id" value={tryModelId} onChange={(e) => setTryModelId(e.target.value)} style={{ minWidth: 320 }} />
          <button className="btn ghost sm" onClick={tryIneligible}>Attempt add</button>
        </div>
        {tryError && (
          <div className="banner show" style={{ display: "flex", marginTop: 10 }}>
            <span>⛔</span>
            <span><b>Rejected by the server:</b> {tryError}</span>
          </div>
        )}
        {tryOk && <div className="note" style={{ marginTop: 8 }}>{tryOk}</div>}
      </div>

      <div className="card">
        <div className="h3"><span>Within-slot blend preview <FieldHelp term="within_slot_blend" /> <span className="note">agreement/dispersion of the components' own predictions — never vote share</span></span></div>
        {detail.blend_preview.available ? (
          <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
            <div style={{ textAlign: "center" }}>
              <div className="note">Blended signal</div>
              <div className="mono" style={{ fontWeight: 700, fontSize: "1.6rem" }}>{fmtSignal(detail.blend_preview.signal)}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div className="note">Confidence (agreement) <FieldHelp term="agreement_dispersion" /></div>
              <div className="mono" style={{ fontWeight: 700, fontSize: "1.6rem" }}>{detail.blend_preview.confidence !== null ? `${(detail.blend_preview.confidence * 100).toFixed(0)}%` : "—"}</div>
            </div>
            <div className="note" style={{ maxWidth: 360 }}>
              Computed from each component's segment-mean predicted value on its own latest run date (n={detail.blend_preview.n_components}) —
              a preview of blend behavior, <b>not</b> a per-stock signal. Per-stock signals still come from Coverage's
              cross-segment blend, which is a different mechanism with confidence deliberately reported as <code>null</code>
              (F6.6 vs G3 — agreement/dispersion is within-slot's concept only; it never applies across a stock's
              several segment memberships).
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <div className="headline">Preview not available</div>
            <div>{detail.blend_preview.reason}</div>
          </div>
        )}
      </div>

      <div className="card">
        <div className="champ-tbd" style={{ background: "var(--surface-2)", border: "1px dashed var(--rule-strong)", borderRadius: 9, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
          <span className="chip">parked</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "0.9rem" }}>Champion resolution</div>
            <div className="note" style={{ fontSize: "0.78rem" }}>How this ensemble competes for the slot's champion is an explicit open decision (ui_spec F6.10) — not hardcoded here.</div>
          </div>
        </div>
      </div>
    </>
  );
}
